import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { OrderDetailsComponent } from './orders/components/order-details/order-details.component';
import { LoginComponent } from './authentication/login/login/login.component';
import { DashboardComponent } from './dashboard/dashboard/dashboard.component';
import { authGuard } from './authentication/auth.guard';
import { EmployeesComponent } from './employees/components/employees/employees.component';
import { DealersComponent } from './dealers/components/dealers/dealers.component';
import { EmployeeDetailsComponent } from './employees/components/employee-details/employee-details.component';
import { PageNotFoundComponent } from './common/components/page-not-found/page-not-found.component';
import { ExpensesComponent } from './expenses/components/expenses/expenses.component';
import { EmployeeTravelReportsComponent } from './expenses/components/employee-travel-reports/employee-travel-reports.component';
import { EmployeeTravelLogComponent } from './expenses/components/employee-travel-log/employee-travel-log.component';
import { AttendanceComponent } from './attendance/components/attendance/attendance.component';
import { AttendanceDetailComponent } from './attendance/components/attendance-detail/attendance-detail.component';
import { ProductsComponent } from './products/components/products/products.component';
import { SettingsComponent } from './settings/components/settings/settings.component';
import { RawMaterialOverviewComponent } from './raw-material-inventory/components/raw-material-overview/raw-material-overview.component';
import { ProductionOverviewComponent } from './production-inventory/components/production-overview/production-overview.component';
import { InventoryOverviewComponent } from './production-inventory/components/inventory-overview/inventory-overview.component';
import { PartyVisitsComponent } from './party-visits/components/party-visits/party-visits.component';
import { EmployeeVisitDetailsComponent } from './party-visits/components/employee-visit-details/employee-visit-details.component';
import { DealerDetailsComponent } from './dealers/components/dealer-details/dealer-details.component';
import { FarmersComponent } from './farmers/component/farmers/farmers.component';
import { FarmerDetailsComponent } from './farmers/component/farmer-details/farmer-details.component';
import { EmployeeFarmersComponent } from './farmers/component/employee-farmers/employee-farmers.component';
import { ProductDetailsComponent } from './products/components/product-details/product-details.component';
import { ProductPackagingDetailsComponent } from './products/components/product-packaging-details/product-packaging-details.component';
import { OrdersOverviewComponent } from './orders/components/orders-overview/orders-overview.component';
import { ShipmentDetailsComponent } from './orders/components/shipment-details/shipment-details.component';
import { PaymentsComponent } from './payments/components/payments/payments.component';
import { PaymentDetailsComponent } from './payments/components/payment-details/payment-details.component';
import { MasterComponent } from './master/master.component';
import { LabourAdvanceLedgerComponent } from './production-inventory/components/labour-advance-ledger/labour-advance-ledger.component';
import { SalesTeamManagementComponent } from './features/sales-team-management/pages/sales-team-management/sales-team-management.component';

const routes: Routes = [
  { path: '', redirectTo: '/dashboard', pathMatch: 'full' },
  { path: 'dashboard', component: DashboardComponent, canActivate: [authGuard] },

  // Employees
  {
    path: 'employees',
    component: EmployeesComponent,
    canActivate: [authGuard],
    data: { module: 'employees' },
  },
  {
    path: 'employees/:id',
    component: EmployeeDetailsComponent,
    canActivate: [authGuard],
    data: { module: 'employees' },
  },

  // Attendance
  {
    path: 'attendance',
    component: AttendanceComponent,
    canActivate: [authGuard],
    data: { module: 'attendance' },
  },
  {
    path: 'attendance/:id',
    component: AttendanceDetailComponent,
    canActivate: [authGuard],
    data: { module: 'attendance' },
  },

  // Dealers
  {
    path: 'dealers',
    component: DealersComponent,
    canActivate: [authGuard],
    data: { module: 'dealers' },
  },
  {
    path: 'dealers/:id',
    component: DealerDetailsComponent,
    canActivate: [authGuard],
    data: { module: 'dealers' },
  },

  // Expenses
  {
    path: 'expenses',
    component: ExpensesComponent,
    canActivate: [authGuard],
    data: { module: 'expenses' },
  },
  {
    path: 'expenses/travel-reports',
    component: EmployeeTravelReportsComponent,
    canActivate: [authGuard],
    data: { module: 'expenses' },
  },
  {
    path: 'expenses/travel-reports/:id',
    component: EmployeeTravelLogComponent,
    canActivate: [authGuard],
    data: { module: 'expenses' },
  },

  // Products
  {
    path: 'products',
    component: ProductsComponent,
    canActivate: [authGuard],
    data: { module: 'inventory' },
  },
  {
    path: 'products/:id',
    component: ProductDetailsComponent,
    canActivate: [authGuard],
    data: { module: 'inventory' },
  },
  {
    path: 'products/:productId/packaging/:packagingId',
    component: ProductPackagingDetailsComponent,
    canActivate: [authGuard],
    data: { module: 'inventory' },
  },

  // Settings
  { path: 'settings', component: SettingsComponent, canActivate: [authGuard] },

  // Raw Material Inventory
  {
    path: 'raw-material-inventory',
    component: RawMaterialOverviewComponent,
    canActivate: [authGuard],
    data: { module: 'inventory' },
  },

  // Production Inventory
  {
    path: 'production-inventory',
    component: ProductionOverviewComponent,
    canActivate: [authGuard],
    data: { module: 'inventory' },
  },

  {
    path: 'production-inventory/labour/:id/advance-ledger',
    component: LabourAdvanceLedgerComponent,
    canActivate: [authGuard],
    data: { module: 'inventory' },
  },

  // Inventory (standalone page with Factory Stock, Finish Goods Stock, Transfers)
  {
    path: 'inventory',
    component: InventoryOverviewComponent,
    canActivate: [authGuard],
    data: { module: 'inventory' },
  },

  {
    path: 'party-visits',
    component: PartyVisitsComponent,
    canActivate: [authGuard],
    data: { module: 'party_visits' },
  },
  {
    path: 'party-visits/employee/:id',
    component: EmployeeVisitDetailsComponent,
    canActivate: [authGuard],
    data: { module: 'party_visits' },
  },

  // Farmers
  {
    path: 'farmers',
    component: FarmersComponent,
    canActivate: [authGuard],
    data: { module: 'farmers' },
  },
  {
    path: 'farmers/employee-farmers',
    component: EmployeeFarmersComponent,
    canActivate: [authGuard],
    data: { module: 'farmers' },
  },
  {
    path: 'farmers/:id',
    component: FarmerDetailsComponent,
    canActivate: [authGuard],
    data: { module: 'farmers' },
  },

  // Orders
  {
    path: 'orders',
    component: OrdersOverviewComponent,
    canActivate: [authGuard],
    data: { module: 'orders' },
  },
  {
    path: 'orders/shipments/:id',
    component: ShipmentDetailsComponent,
    canActivate: [authGuard],
    data: { module: 'orders' },
  },
  {
    path: 'orders/:id',
    component: OrderDetailsComponent,
    canActivate: [authGuard],
    data: { module: 'orders' },
  },

  // Payments
  {
    path: 'payments',
    component: PaymentsComponent,
    canActivate: [authGuard],
    data: { module: 'payments' },
  },
  {
    path: 'payments/:id',
    component: PaymentDetailsComponent,
    canActivate: [authGuard],
    data: { module: 'payments' },
  },

  // Sales Team Management (admin only; the page shows a no-access state on 403)
  {
    path: 'sales-team-management',
    component: SalesTeamManagementComponent,
    canActivate: [authGuard],
  },

  // Master (admin only)
  { path: 'master', component: MasterComponent, canActivate: [authGuard] },

  { path: 'login', component: LoginComponent },
  { path: '**', component: PageNotFoundComponent },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
